import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getMyDashboards } from "../api/client";

/**
 * Route guard for dashboard pages. Asks the backend for the caller's effective
 * dashboard access (role defaults ∪ explicit allows − explicit denies). If they
 * have ANY dashboard granted, they get in; otherwise they're redirected.
 *
 * This replaces the old role-only check, which would have let admins in even
 * when every dashboard had been explicitly denied for them.
 */
export default function DashboardRoute({ children }) {
  const { user, loading } = useAuth();
  const [access, setAccess] = useState({ status: "loading", dashboards: [] });

  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    const load = async () => {
      try {
        const res = await getMyDashboards();
        if (!cancelled) {
          setAccess({
            status: "ready",
            dashboards: res.data?.dashboards || [],
          });
        }
      } catch (e) {
        if (!cancelled) setAccess({ status: "error", dashboards: [] });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading || access.status === "loading") {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Any dashboard (main, leadership, organization, my_team) lets the user
  // through the top-level /dashboard guard. Individual pages still enforce
  // their specific dashboard via the backend.
  const dashboardKeys = ["main", "leadership", "organization", "my_team"];
  const allowed = access.dashboards.some((d) => dashboardKeys.includes(d));

  if (!allowed) {
    return <Navigate to="/welcome" replace />;
  }

  return children;
}
